import { useState, type KeyboardEvent, type ReactNode } from "react";
import clsx from "clsx";
import { accommodationColorPickerOptions } from "../theme/colors";

// --- FormLabel ---
export function FormLabel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <label className={clsx("form-label", className)}>{children}</label>;
}

// --- FormInput ---
export function FormInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  type = "text",
  placeholder,
  step,
  min,
  disabled,
  className,
  autoFocus,
  "data-testid": dataTestId,
}: {
  value: string | number;
  onChange: (val: string) => void;
  /** For fields that commit on blur rather than on every keystroke. */
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  step?: string;
  min?: string;
  disabled?: boolean;
  className?: string;
  /** Marks the field as the modal's initial focus target */
  autoFocus?: boolean;
  "data-testid"?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      step={step}
      min={min}
      disabled={disabled}
      data-autofocus={autoFocus ? "" : undefined}
      data-testid={dataTestId}
      className={clsx(
        "input-base",
        type === "date" && "min-w-0 pr-10 tabular-nums",
        className,
      )}
    />
  );
}

// --- FormTextarea ---
export function FormTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      disabled={disabled}
      className="input-base resize-y min-h-[60px]"
    />
  );
}

// --- FormSelect ---
export function FormSelect({
  value,
  onChange,
  options,
  disabled,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  "data-testid"?: string;
  "aria-label"?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      className="select-base pr-8"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

// --- FormRow ---
export function FormRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-3 sm:flex-row">{children}</div>;
}

// --- FormField (wrapper with label) ---
export function FormField({
  label,
  children,
  flex,
}: {
  label: string;
  children: ReactNode;
  flex?: number;
}) {
  return (
    <div style={{ flex }} className="min-w-0">
      <FormLabel>{label}</FormLabel>
      {children}
    </div>
  );
}

// --- TagInput ---
export function TagInput({
  tags,
  onChange,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
}) {
  const [input, setInput] = useState("");

  const addTag = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput("");
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Type and press Enter..."
          className="input-base flex-1"
        />
      </div>
      {tags.length > 0 && (
        <div className="flex gap-1.5 flex-wrap mt-2">
          {tags.map((tag) => (
            <span key={tag} className="tag py-[3px]">
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="flex items-center bg-transparent border-none cursor-pointer p-0 text-tertiary text-sm leading-none hover:text-primary"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// --- DayOfWeekCheckboxes ---
const DAYS_OF_WEEK = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function DayOfWeekCheckboxes({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (days: string[]) => void;
}) {
  const toggle = (day: string) => {
    if (selected.includes(day)) {
      onChange(selected.filter((d) => d !== day));
    } else {
      onChange([...selected, day]);
    }
  };

  return (
    <div className="flex gap-1">
      {DAYS_OF_WEEK.map((day, i) => {
        const active = selected.includes(day);
        return (
          <button
            key={day}
            type="button"
            onClick={() => toggle(day)}
            className={clsx(
              "flex-1 py-1.5 text-xs font-semibold font-sans rounded-md cursor-pointer transition-all",
              active
                ? "border border-primary bg-primary text-white"
                : "border border-border bg-white text-secondary hover:bg-gray-50",
            )}
          >
            {DAY_LABELS[i]}
          </button>
        );
      })}
    </div>
  );
}

// --- ColorPicker ---
export interface ColorPickerOption {
  index: number;
  bg: string;
  ring: string;
}

export function ColorPicker({
  selectedIndex,
  onChange,
  options = accommodationColorPickerOptions,
  ariaLabel,
}: {
  selectedIndex: number;
  onChange: (index: number) => void;
  /** Defaults to the accommodation palette; legs pass their own. */
  options?: readonly ColorPickerOption[];
  ariaLabel?: string;
}) {
  return (
    <div className="flex gap-2">
      {options.map((c) => (
        <button
          key={c.index}
          type="button"
          aria-label={
            ariaLabel ? `${ariaLabel} ${c.index + 1}` : `Colour ${c.index + 1}`
          }
          aria-pressed={selectedIndex === c.index}
          onClick={() => onChange(c.index)}
          className="size-8 rounded-full cursor-pointer transition-all hover:scale-110"
          style={{
            background: c.bg,
            border:
              selectedIndex === c.index
                ? `3px solid ${c.ring}`
                : "2px solid var(--color-border)",
            boxShadow:
              selectedIndex === c.index
                ? `0 0 0 2px white, 0 0 0 4px ${c.ring}`
                : "none",
          }}
        />
      ))}
    </div>
  );
}

// --- Section Toggle ---
export function FormSection({
  label,
  enabled,
  onToggle,
  children,
}: {
  label: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <div
        className={clsx(
          "flex items-center justify-between",
          enabled && "mb-2.5",
        )}
      >
        <FormLabel className="!mb-0">{label}</FormLabel>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className={clsx(
            "relative w-9 h-5 rounded-full border-none cursor-pointer transition-colors p-0",
            enabled ? "bg-primary" : "bg-border",
          )}
        >
          <div
            className={clsx(
              "absolute top-0.5 size-4 rounded-full bg-white transition-[left]",
              enabled ? "left-[18px]" : "left-0.5",
            )}
          />
        </button>
      </div>
      {enabled && children}
    </div>
  );
}
